import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "./Header";
import { api } from "../server";

const money = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(Number.isFinite(Number(n)) ? Number(n) : 0);

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function MonthlyReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/admin/reports/monthly?year=${year}&month=${month}`);
      setReport(data || null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const summary = report?.summary || {};
  const byDay = report?.byDay || [];
  const topProducts = report?.topProducts || [];

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) arr.push(y);
    return arr;
  }, [now]);

  const peakDay = useMemo(() => {
    if (!byDay.length) return null;
    return byDay.reduce((p, c) => (c.revenue > (p?.revenue || 0) ? c : p), null);
  }, [byDay]);

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Monthly Report</h3>
                <div className="text-secondary lh-1">Daily breakdown • Top products • Revenue</div>
              </div>
              <div className="d-flex gap-1 align-items-center">
                <button className="btn btn-light border px-4" onClick={fetchReport}>
                  <i className="bi bi-arrow-clockwise me-2" />Refresh
                </button>
                <button className="btn btn-light border px-4" onClick={() => window.print()}>
                  <i className="bi bi-printer me-2" />Print
                </button>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 mb-2">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary" style={{ width: 40, height: 40 }}>
                    <i className="bi bi-calendar3" />
                  </div>
                  <div>
                    <div className="fw-semibold lh-1">Pick a period</div>
                    <div className="text-secondary small">Choose month and year</div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-6 col-md-4">
                    <label className="form-label mb-1 fw-normal">Month</label>
                    <select className="form-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                      {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div className="col-6 col-md-4">
                    <label className="form-label mb-1 fw-normal">Year</label>
                    <select className="form-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                      {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
            ) : (
              <>
                <div className="row g-2 mb-2">
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-cart" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">Total Orders</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1">{summary.totalOrders || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-check2-circle" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">Delivered</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1 text-success">{summary.delivered || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-danger-subtle text-danger" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-x-circle" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">Cancelled</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1 text-danger">{summary.cancelled || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-cash-stack" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">Revenue</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1 text-success">{money(summary.revenue)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {peakDay && (
                  <div className="card border-0 shadow-sm rounded-4 mb-2">
                    <div className="card-body p-3 p-md-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-trophy" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">Peak Revenue Day</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1">{peakDay.date}</div>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="text-secondary small lh-1">Revenue</div>
                        <div className="fs-5 fw-semibold lh-1 mt-1 text-success">{money(peakDay.revenue)}</div>
                        <div className="small text-secondary mt-1">{peakDay.delivered} delivered • {peakDay.orders} orders</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="card border-0 shadow-sm rounded-4 mb-2">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info" style={{ width: 40, height: 40 }}>
                        <i className="bi bi-calendar-day" />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">Daily Breakdown</div>
                        <div className="text-secondary small">Orders, deliveries, and revenue per day</div>
                      </div>
                    </div>
                    {byDay.length === 0 ? (
                      <div className="text-center py-4 text-secondary">
                        <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />No data for this month
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Date</th>
                              <th className="text-end">Orders</th>
                              <th className="text-end">Delivered</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {byDay.map((d, i) => (
                              <tr key={i}>
                                <td className="fw-semibold">{d.date}</td>
                                <td className="text-end">{d.orders}</td>
                                <td className="text-end text-success fw-semibold">{d.delivered}</td>
                                <td className="text-end fw-semibold">{money(d.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success" style={{ width: 40, height: 40 }}>
                        <i className="bi bi-stars" />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">Top Products</div>
                        <div className="text-secondary small">Best sellers by revenue</div>
                      </div>
                    </div>
                    {topProducts.length === 0 ? (
                      <div className="text-center py-4 text-secondary">
                        <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />No products sold this month
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 60 }}>#</th>
                              <th>Product</th>
                              <th className="text-end">Quantity</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topProducts.map((p, i) => (
                              <tr key={i}>
                                <td>
                                  <span className={`badge ${i === 0 ? "bg-warning-subtle text-warning" : i < 3 ? "bg-info-subtle text-info" : "bg-secondary-subtle text-secondary"}`}>
                                    {i + 1}
                                  </span>
                                </td>
                                <td className="fw-semibold">{p.productName}</td>
                                <td className="text-end">{p.quantity}</td>
                                <td className="text-end fw-semibold">{money(p.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </main>
      </div>
    </>
  );
}

export default MonthlyReport;
